import { X, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '~/components/ui';

type ProductSettingModalProps = {
  productName?: string;
  initialCriticalStockLevel: number;
  initialLowStockLevel: number;
  stockQuantity: number;
  replenishmentRate: number;
  onClose: () => void;
  onSave: (criticalStockLevel: number, lowStockLevel: number) => void;
  isSaving?: boolean;
};

export function ProductSettingModal({
  productName,
  initialCriticalStockLevel,
  initialLowStockLevel,
  stockQuantity,
  replenishmentRate,
  onClose,
  onSave,
  isSaving,
}: ProductSettingModalProps) {
  const { t } = useTranslation('my-product');
  const [criticalStockLevel, setCriticalStockLevel] = useState<number>(
    initialCriticalStockLevel
  );
  const [lowStockLevel, setLowStockLevel] =
    useState<number>(initialLowStockLevel);
  return (
    <div className="bg-neut-0 rounded-lg shadow-xl w-2xl">
      <div className="px-6 py-4 border-b border-neut-200 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-neut-prim">
            {t('stock_monitoring.title')}
          </h2>
          <p className="text-sm text-neut-500 mt-1">{productName}</p>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-neut-100 rounded-lg transition-colors"
        >
          <X size={20} />
        </button>
      </div>

      <div className="p-6 space-y-6">
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-neut-900">
            {t('stock_monitoring.thresholds.title')}
          </h3>
          <p className="text-xs text-neut-600">
            {t('stock_monitoring.thresholds.subtitle')}
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Input
                label={t('stock_monitoring.thresholds.critical_stock_level')}
                type="number"
                name={'criticalStockLevel'}
                value={criticalStockLevel?.toString()}
                onChange={(_, value) => setCriticalStockLevel(Number(value))}
              />
              <p className="text-xs text-neut-500 mt-1">
                {t('stock_monitoring.thresholds.critical_alert')}
              </p>
            </div>
            <div>
              <Input
                label={t('stock_monitoring.thresholds.low_stock_level')}
                type="number"
                name={'lowStockLevel'}
                value={lowStockLevel?.toString()}
                onChange={(_, value) => setLowStockLevel(Number(value))}
              />
              <p className="text-xs text-neut-500 mt-1">
                {t('stock_monitoring.thresholds.low_stock_alert')}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-2 mb-3">
            <TriangleAlert
              size={16}
              className="text-blue-600 shrink-0 mt-0.5"
            />
            <h4 className="text-sm font-semibold text-blue-900">
              {t('stock_monitoring.status_categories')}
            </h4>
          </div>
          <div className="space-y-1.5 text-xs text-blue-800 ml-6">
            <div>
              <span className="font-semibold text-error-600">
                {t('stock_monitoring.status_categories.critical')}
              </span>
              <span className="ml-1">
                {t('stock_monitoring.status_categories.critical_range', {
                  value: criticalStockLevel,
                })}
              </span>
            </div>
            <div>
              <span className="font-semibold text-warning-600">
                {t('stock_monitoring.status_categories.low_stock')}
              </span>
              <span className="ml-1">
                {t('stock_monitoring.status_categories.low_stock_range', {
                  min: criticalStockLevel,
                  max: lowStockLevel,
                })}
              </span>
            </div>
            <div>
              <span className="font-semibold text-success-600">
                {t('stock_monitoring.status_categories.healthy')}
              </span>
              <span className="ml-1">
                {t('stock_monitoring.status_categories.healthy_range', {
                  value: lowStockLevel,
                })}
              </span>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-base font-semibold text-neut-prim mb-3">
            {t('stock_monitoring.current_status.title')}
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-neut-50 rounded-lg border border-neut-200">
              <p className="text-xs text-neut-600 mb-1">
                {t('stock_monitoring.current_status.stock_quantity')}
              </p>
              <p className="text-lg font-semibold text-neut-prim">
                {stockQuantity?.toLocaleString()}{' '}
                {t('stock_monitoring.current_status.units')}
              </p>
            </div>
            <div className="p-3 bg-neut-50 rounded-lg border border-neut-200">
              <p className="text-xs text-neut-600 mb-1">
                {t('stock_monitoring.current_status.replenishment_rate')}
              </p>
              <p className="text-lg font-semibold text-neut-prim">
                {replenishmentRate?.toLocaleString()}
                {t('stock_monitoring.current_status.per_week')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-4 border-t border-neut-200 flex items-center justify-end gap-3">
        <Button onClick={onClose} width="6rem" variant="secondary">
          {t('stock_monitoring.buttons.cancel')}
        </Button>
        <Button
          disabled={isSaving}
          onClick={() => onSave(criticalStockLevel, lowStockLevel)}
          width="8rem"
        >
          {isSaving
            ? t('stock_monitoring.buttons.saving')
            : t('stock_monitoring.buttons.save_settings')}
        </Button>
      </div>
    </div>
  );
}
